import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = process.env.REACT_APP_BACKEND_URL;
export const inr = (v) => (v === null || v === undefined ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export async function ahApi(path, token, body = {}) {
  const r = await fetch(`${API}/api/franchise-exit/annexure-h${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, ...body }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "object" ? d.detail.message : d.detail || "Request failed");
  return d;
}

const today = () => new Date().toISOString().slice(0, 10);

export function PaymentsDialog({ open, onClose, exitId, token, row, editable, onChanged }) {
  const [f, setF] = useState({ amount: "", date: today(), mode: "BANK_TRANSFER", reference: "", confirmation: "EVIDENCE", manual_reason: "" });
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const rid = row?.row_id;
  useEffect(() => { setF((x) => ({ ...x, amount: "", reference: "", manual_reason: "" })); setFile(null); setFileKey((k) => k + 1); }, [rid]);
  if (!row) return null;
  const add = async () => {
    setBusy(true);
    try {
      let att = {};
      if (file) {
        const fd = new FormData(); fd.append("token", token); fd.append("file", file);
        const r = await fetch(`${API}/api/franchise-exit/annexure-h/upload-receipt/${exitId}`, { method: "POST", body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Upload failed");
        att = { attachment_path: d.path, attachment_name: d.name };
      }
      await ahApi(`/payment/add/${exitId}`, token, { row_id: row.row_id, ...f, amount: parseFloat(f.amount), ...att });
      toast.success("Payment recorded");
      setF({ ...f, amount: "", reference: "", manual_reason: "" }); setFile(null); setFileKey((k) => k + 1); onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const voidP = async (p) => {
    const reason = window.prompt("Reason to void this payment entry:");
    if (!reason) return;
    try { await ahApi(`/payment/void/${exitId}`, token, { row_id: row.row_id, payment_id: p.id, reason }); toast.success("Payment voided"); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="ah-payments-dialog">
        <DialogHeader><DialogTitle>Salary payments — {row.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600">Actual payable {inr(row.salary_payable)} · Paid {inr(row.salary_paid)} · Balance <b>{inr(row.balance)}</b></p>
        <table className="w-full text-xs" data-testid="ah-payments-table">
          <thead className="bg-slate-50"><tr><th className="p-1 text-left">Date</th><th className="p-1 text-right">Amount</th><th className="p-1">Mode</th><th className="p-1 text-left">Reference / Receipt</th><th className="p-1 text-left">Verification</th><th /></tr></thead>
          <tbody>
            {(row.payments || []).map((p) => (
              <tr key={p.id} className={`border-t ${p.voided ? "line-through text-slate-400" : ""}`} data-testid={`ah-payment-${p.id}`}>
                <td className="p-1">{p.date || "—"}</td><td className="p-1 text-right">{inr(p.amount)}</td><td className="p-1 text-center">{p.mode}</td>
                <td className="p-1">{p.reference || "—"} {p.attachment_path && <a className="text-blue-700 underline" target="_blank" rel="noreferrer" href={`${API}/api/franchise-exit/annexure-h/receipt?token=${encodeURIComponent(token)}&path=${encodeURIComponent(p.attachment_path)}`}>receipt</a>}</td>
                <td className={`p-1 ${p.confirmation === "MANUAL" ? "text-amber-700" : "text-emerald-700"}`}>{p.verification_label} · {p.verified_by}{p.manual_reason ? ` — ${p.manual_reason}` : ""}</td>
                <td className="p-1">{editable && !p.voided && p.id !== "legacy" && <button className="text-red-600" onClick={() => voidP(p)} data-testid={`ah-void-${p.id}`}>void</button>}</td>
              </tr>
            ))}
            {!(row.payments || []).length && <tr><td colSpan={6} className="p-2 text-slate-500">No payments recorded.</td></tr>}
          </tbody>
        </table>
        {editable && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t pt-2" data-testid="ah-payment-form">
            <Input type="number" step="0.01" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="ah-pay-amount" />
            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="ah-pay-date" />
            <select className="h-9 rounded border px-2 text-sm" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="ah-pay-mode">
              {["BANK_TRANSFER", "UPI", "CHEQUE", "CASH", "OTHER"].map((m) => <option key={m}>{m}</option>)}
            </select>
            <Input placeholder="UTR / cheque / voucher no." value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="ah-pay-reference" />
            <select className="h-9 rounded border px-2 text-sm" value={f.confirmation} onChange={(e) => setF({ ...f, confirmation: e.target.value })} data-testid="ah-pay-confirmation">
              <option value="EVIDENCE">Evidence (reference / receipt)</option><option value="MANUAL">SA manual confirmation</option>
            </select>
            {f.confirmation === "MANUAL" && <Input className="md:col-span-2" placeholder="Reason for manual confirmation (mandatory)" value={f.manual_reason} onChange={(e) => setF({ ...f, manual_reason: e.target.value })} data-testid="ah-pay-manual-reason" />}
            <input key={fileKey} type="file" accept="image/*,.pdf" className="text-xs" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ah-pay-receipt" />
            <Button size="sm" onClick={add} disabled={busy || !f.amount} data-testid="ah-pay-save">Record payment</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_ADD = { mode: "MASTER", employee_mobile: "", name: "", designation: "", salary_payable: "", remarks: "" };
export function AddEmployeeDialog({ open, onClose, exitId, token, onAdded }) {
  const [f, setF] = useState(EMPTY_ADD);
  useEffect(() => { if (open) setF(EMPTY_ADD); }, [open]);
  const add = async () => {
    try {
      await ahApi(`/add/${exitId}`, token, { employee_mobile: f.mode === "MASTER" ? f.employee_mobile : null, name: f.name || null,
        designation: f.designation, salary_payable: parseFloat(f.salary_payable || 0), remarks: f.remarks, reason: "Added by Super Admin" });
      toast.success("Employee added to Annexure H"); onAdded(); onClose();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="ah-add-dialog">
        <DialogHeader><DialogTitle>Add employee to Annexure H</DialogTitle></DialogHeader>
        <select className="h-9 rounded border px-2 text-sm" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="ah-add-mode">
          <option value="MASTER">Existing employee (Employee Master)</option><option value="MANUAL">Manual entry (not in Employee Master)</option>
        </select>
        {f.mode === "MASTER" && <Input placeholder="Employee mobile number" value={f.employee_mobile} onChange={(e) => setF({ ...f, employee_mobile: e.target.value })} data-testid="ah-add-mobile" />}
        <Input placeholder={f.mode === "MASTER" ? "Name (optional — from master)" : "Employee name"} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ah-add-name" />
        <Input placeholder="Designation" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} data-testid="ah-add-designation" />
        <Input type="number" step="0.01" placeholder="Actual salary payable" value={f.salary_payable} onChange={(e) => setF({ ...f, salary_payable: e.target.value })} data-testid="ah-add-payable" />
        <Input placeholder="Remarks" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="ah-add-remarks" />
        <Button onClick={add} data-testid="ah-add-save">Add employee</Button>
      </DialogContent>
    </Dialog>
  );
}

export function PayrollPreviewDialog({ open, onClose, data }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="ah-payroll-preview-dialog">
        <DialogHeader><DialogTitle>Refresh from Payroll — Preview Only ({data?.salary_month})</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600">Nothing is changed. Payroll total {inr(data?.payroll_total)} · state {data?.payroll_state}.</p>
        <table className="w-full text-xs"><thead className="bg-slate-50"><tr><th className="p-1 text-left">Employee</th><th className="p-1 text-right">Payroll net</th><th className="p-1 text-right">Annexure H payable</th><th className="p-1">Status</th></tr></thead>
          <tbody>{(data?.rows || []).map((r, i) => <tr key={i} className="border-t"><td className="p-1">{r.name}</td><td className="p-1 text-right">{inr(r.payroll_net)}</td><td className="p-1 text-right">{inr(r.annexure_payable)}</td><td className="p-1 text-center">{r.status}</td></tr>)}</tbody></table>
      </DialogContent>
    </Dialog>
  );
}
